import { useCallback, useMemo, useRef, useState } from 'react';

import type { ColumnDefinition } from '@/features/table/components/ColumnPicker';
import type { ColDef } from 'ag-grid-community';

// Compare two arrays by value (order matters)
const arraysEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every((val, idx) => val === b[idx]);
};

export interface UseColumnVisibilityProps<T> {
  columnDefs: ColDef<T>[];
  savedVisibleColumnIds?: string[] | null;
}

export interface UseColumnVisibilityResult<T> {
  visibleColumnDefs: ColDef<T>[];
  allColumns: ColumnDefinition[];
  visibleColumnIds: string[];
  handleVisibilityChange: (visibleIds: string[]) => void;
}

const getColumnId = <T>(colDef: ColDef<T>): string => {
  if (colDef.colId) return colDef.colId;
  if (colDef.field) return colDef.field as string;
  // Use headerName as fallback ID for columns with only valueGetter
  if (typeof colDef.headerName === 'string') {
    return colDef.headerName.toLowerCase().replace(/\s+/g, '-');
  }
  return '';
};

const getColumnLabel = <T>(colDef: ColDef<T>): string => {
  if (typeof colDef.headerName === 'string') {
    return colDef.headerName;
  }
  return colDef.field ?? colDef.colId ?? 'Unknown';
};

export const useColumnVisibility = <T>({
  columnDefs,
  savedVisibleColumnIds
}: UseColumnVisibilityProps<T>): UseColumnVisibilityResult<T> => {
  const allColumns = useMemo<ColumnDefinition[]>(() => {
    return columnDefs
      .filter((col) => getColumnId(col))
      .map((col) => {
        const id = getColumnId(col);
        const isActionsColumn = id === 'actions' || col.pinned === 'right';
        return {
          id,
          label: getColumnLabel(col),
          defaultVisible: col.hide !== true,
          canHide: !isActionsColumn
        };
      });
  }, [columnDefs]);

  const defaultVisibleIds = allColumns.filter((col) => col.defaultVisible !== false).map((col) => col.id);

  // Track user's manual column visibility changes (null = user hasn't changed anything yet)
  const [userVisibleColumnIds, setUserVisibleColumnIds] = useState<string[] | null>(null);

  // Derive visible column IDs: user changes > saved layout > defaults
  const derivedVisibleIds = useMemo(() => {
    if (userVisibleColumnIds !== null) {
      return userVisibleColumnIds;
    }
    if (savedVisibleColumnIds && savedVisibleColumnIds.length > 0) {
      return savedVisibleColumnIds;
    }
    return defaultVisibleIds;
  }, [userVisibleColumnIds, savedVisibleColumnIds, defaultVisibleIds]);

  // Stabilize array reference - only change if values actually change
  // This prevents unnecessary re-renders of visibleColumnDefs when save updates cache
  const prevVisibleIdsRef = useRef<string[]>(derivedVisibleIds);
  const visibleColumnIds = useMemo(() => {
    if (arraysEqual(prevVisibleIdsRef.current, derivedVisibleIds)) {
      return prevVisibleIdsRef.current;
    }
    prevVisibleIdsRef.current = derivedVisibleIds;
    return derivedVisibleIds;
  }, [derivedVisibleIds]);

  const handleVisibilityChange = useCallback((visibleIds: string[]) => {
    setUserVisibleColumnIds(visibleIds);
  }, []);

  const visibleColumnDefs = useMemo<ColDef<T>[]>(() => {
    return columnDefs.map((col) => {
      const id = getColumnId(col);
      if (!id) return col;

      const columnDef = allColumns.find((c) => c.id === id);
      if (columnDef?.canHide === false) {
        return { ...col, hide: false };
      }

      const isVisible = visibleColumnIds.includes(id);
      return { ...col, hide: !isVisible };
    });
  }, [columnDefs, visibleColumnIds, allColumns]);

  return {
    visibleColumnDefs,
    allColumns,
    visibleColumnIds,
    handleVisibilityChange
  };
};
